/**
 * Focus-aware keyboard shortcuts.
 *
 * Single-letter shortcuts are the fastest thing an app can offer and the
 * easiest to get wrong: press `c` while writing a note and the note stops and
 * a dialog opens instead. So anything with a modifier is left to the browser,
 * and anything typed into a field is left to the field.
 */

/** The parts of an event these need. Anything more would only be untestable. */
export type ShortcutEvent = {
  key: string
  altKey?: boolean
  ctrlKey?: boolean
  metaKey?: boolean
  isComposing?: boolean
  target: EventTarget | null
}

export function isBareShortcut(event: ShortcutEvent, key: string): boolean {
  if (event.key.toLowerCase() !== key.toLowerCase()) return false
  if (event.altKey === true || event.ctrlKey === true || event.metaKey === true)
    return false
  // Mid-composition, a keystroke belongs to the input method, not to us.
  if (event.isComposing === true) return false

  return !isTyping(event.target)
}

/**
 * Whether focus is somewhere that swallows ordinary keys.
 *
 * Duck-typed rather than checked with `instanceof Element` so this stays a
 * plain function that can be tested without standing up a whole DOM. The two
 * properties it reads are the two the browser actually provides.
 */
export function isTyping(target: EventTarget | null): boolean {
  if (target === null) return false

  const element = target as {
    tagName?: string
    closest?: (selector: string) => unknown
  }
  const tag = element.tagName?.toLowerCase()
  if (tag === undefined) return false
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true

  // A contenteditable is not an input element, which is exactly why checking
  // tag names alone gets this wrong.
  const editable = element.closest?.('[contenteditable="true"]')
  return editable !== null && editable !== undefined
}
